import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  Icon,
  Divider,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import { 
  FiTarget, 
  FiUsers, 
  FiTrendingUp, 
  FiAward, 
  FiClock, 
  FiShield,
  FiArrowRight,
  FiCheck,
  FiX
} from "react-icons/fi";
import { NavLink } from "react-router-dom";
import Footer from "../../components/Footer";

const ComparisonCard = ({ title, traditional, skillback, isHighlight = false }) => (
  <Box
    bg="white"
    p={6}
    borderRadius="xl"
    border="1px solid"
    borderColor={isHighlight ? "blue.200" : "gray.200"}
    shadow={isHighlight ? "lg" : "sm"}
    position="relative"
  >
    {isHighlight && (
      <Badge
        position="absolute"
        top="-10px"
        left="50%"
        transform="translateX(-50%)"
        colorScheme="blue"
        variant="solid"
        px={3}
        py={1}
        borderRadius="full"
        fontSize="xs"
      >
        Key Differentiator
      </Badge>
    )}
    <VStack spacing={4} align="stretch">
      <Text fontWeight="600" color="gray.900" fontSize="lg" textAlign="center">
        {title}
      </Text>
      <Divider />
      <SimpleGrid columns={2} spacing={4}>
        <VStack spacing={3} align="start">
          <Text fontSize="sm" fontWeight="600" color="gray.600">
            Traditional Approach
          </Text>
          <HStack align="start" spacing={2}>
            <Icon as={FiX} color="red.500" mt={0.5} flexShrink={0} />
            <Text fontSize="sm" color="gray.700" lineHeight="1.5">
              {traditional}
            </Text>
          </HStack>
        </VStack>
        <VStack spacing={3} align="start">
          <Text fontSize="sm" fontWeight="600" color="blue.600">
            SkillBack Advantage
          </Text>
          <HStack align="start" spacing={2}>
            <Icon as={FiCheck} color="green.500" mt={0.5} flexShrink={0} />
            <Text fontSize="sm" color="gray.700" lineHeight="1.5">
              {skillback}
            </Text>
          </HStack>
        </VStack>
      </SimpleGrid>
    </VStack>
  </Box>
);

const StatCard = ({ icon, number, label, description }) => (
  <Box
    bg="white"
    p={6}
    borderRadius="xl"
    border="1px solid"
    borderColor="gray.200"
    shadow="sm"
    textAlign="center"
  >
    <VStack spacing={4}>
      <Box
        w={12}
        h={12}
        bg="blue.50"
        borderRadius="xl"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Icon as={icon} color="blue.600" boxSize={6} />
      </Box>
      <VStack spacing={1}>
        <Text fontSize="3xl" fontWeight="700" color="blue.600">
          {number}
        </Text>
        <Text fontSize="sm" fontWeight="600" color="gray.900">
          {label}
        </Text>
        <Text fontSize="xs" color="gray.600" textAlign="center">
          {description}
        </Text>
      </VStack>
    </VStack>
  </Box>
);

const WhyChooseUs = () => {
  const bgColor = useColorModeValue("gray.50", "gray.800");

  return (
    <Box bg="white" minH="100vh">
      {/* Hero Section */}
      <Box bg={bgColor} py={{ base: "4rem", md: "6rem" }}>
        <Container maxW="6xl" px={{ base: 4, md: 8 }}>
          <VStack spacing="2rem" textAlign="center" maxW="800px" mx="auto">
            <Badge
              colorScheme="blue"
              variant="subtle"
              px={4}
              py={2}
              borderRadius="full"
              fontSize="sm"
              fontWeight="600"
            >
              Research-Backed Approach
            </Badge>
            <Heading as="h1" size="2xl" color="gray.900" fontWeight="700" lineHeight="1.1">
              Why SkillBack Outperforms Traditional Resume Solutions
            </Heading>
            <Text fontSize="xl" color="gray.600" lineHeight="1.6">
              Discover how our SME-trained approach delivers measurably better results 
              than conventional resume builders and expensive career coaches
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Research Stats */}
      <Container maxW="6xl" px={{ base: 4, md: 8 }} py={{ base: "4rem", md: "6rem" }}>
        <VStack spacing="4rem">
          <VStack spacing="1.5rem" textAlign="center" maxW="700px" mx="auto">
            <Heading as="h2" size="xl" color="gray.900" fontWeight="700">
              The Data Speaks for Itself
            </Heading>
            <Text fontSize="lg" color="gray.600" lineHeight="1.6">
              Independent research and user studies reveal significant advantages over traditional methods
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} w="full">
            <StatCard
              icon={FiTrendingUp}
              number="2.3x"
              label="Higher Response Rate"
              description="Compared to generic resume builders"
            />
            <StatCard
              icon={FiClock}
              number="89%"
              label="Time Reduction"
              description="Less time spent per application"
            />
            <StatCard
              icon={FiTarget}
              number="94%"
              label="ATS Pass Rate"
              description="Successfully parsed by applicant tracking systems"
            />
            <StatCard
              icon={FiAward}
              number="73%"
              label="Interview Rate"
              description="Of users receive interview invitations"
            />
          </SimpleGrid>
        </VStack>
      </Container>

      {/* Comparison Section */}
      <Box bg={bgColor} py={{ base: "4rem", md: "6rem" }}>
        <Container maxW="6xl" px={{ base: 4, md: 8 }}>
          <VStack spacing="4rem">
            <VStack spacing="1.5rem" textAlign="center" maxW="700px" mx="auto">
              <Heading as="h2" size="xl" color="gray.900" fontWeight="700">
                SkillBack vs. Traditional Solutions
              </Heading>
              <Text fontSize="lg" color="gray.600" lineHeight="1.6">
                See how our SME-trained approach compares to conventional resume builders and career coaching
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} w="full">
              <ComparisonCard
                title="Content Quality & Expertise"
                traditional="Generic templates with basic suggestions. No industry-specific knowledge or SME input."
                skillback="SME-trained models with deep industry expertise. Content crafted by subject matter experts across tech domains."
                isHighlight={true}
              />
              <ComparisonCard
                title="Personalization & Targeting"
                traditional="One-size-fits-all approach. Manual keyword stuffing without context understanding."
                skillback="Job-specific optimization with intelligent skill matching and contextual bullet point generation."
              />
              <ComparisonCard
                title="ATS Optimization"
                traditional="Basic formatting with limited ATS compatibility. Often fails parsing tests."
                skillback="94% ATS pass rate with advanced parsing optimization and recruiter-tested formats."
              />
              <ComparisonCard
                title="Speed & Efficiency"
                traditional="Hours of manual work per application. Requires extensive research and writing."
                skillback="30-second optimization per job. Automated analysis with instant, expert-level results."
                isHighlight={true}
              />
              <ComparisonCard
                title="Cost Effectiveness"
                traditional="$200-500 per session with career coaches. Limited ongoing support and updates."
                skillback="Unlimited optimizations for a fraction of coaching costs. Continuous model improvements."
              />
              <ComparisonCard
                title="Measurable Results"
                traditional="Subjective feedback with no performance tracking. Unclear ROI on investment."
                skillback="Data-driven results with 2.3x higher response rates and 73% interview success rate."
              />
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Research Methodology */}
      <Container maxW="6xl" px={{ base: 4, md: 8 }} py={{ base: "4rem", md: "6rem" }}>
        <VStack spacing="4rem">
          <VStack spacing="1.5rem" textAlign="center" maxW="700px" mx="auto">
            <Heading as="h2" size="xl" color="gray.900" fontWeight="700">
              Our Research-Backed Methodology
            </Heading>
            <Text fontSize="lg" color="gray.600" lineHeight="1.6">
              Built on extensive analysis of successful resumes and recruiter preferences
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
            <Box
              bg="white"
              p={6}
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.200"
              shadow="sm"
            >
              <VStack spacing={4} align="start">
                <Box
                  w={10}
                  h={10}
                  bg="blue.50"
                  borderRadius="lg"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={FiUsers} color="blue.600" boxSize={5} />
                </Box>
                <VStack align="start" spacing={2}>
                  <Text fontWeight="600" color="gray.900">
                    10,000+ Resume Analysis
                  </Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="1.5">
                    Analyzed successful resumes from top-tier companies across technology, 
                    finance, healthcare, and consulting sectors.
                  </Text>
                </VStack>
              </VStack>
            </Box>

            <Box
              bg="white"
              p={6}
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.200"
              shadow="sm"
            >
              <VStack spacing={4} align="start">
                <Box
                  w={10}
                  h={10}
                  bg="green.50"
                  borderRadius="lg"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={FiShield} color="green.600" boxSize={5} />
                </Box>
                <VStack align="start" spacing={2}>
                  <Text fontWeight="600" color="gray.900">
                    500+ Recruiter Interviews
                  </Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="1.5">
                    Direct feedback from hiring managers at Fortune 500 companies 
                    on what makes resumes stand out in their review process.
                  </Text>
                </VStack>
              </VStack>
            </Box>

            <Box
              bg="white"
              p={6}
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.200"
              shadow="sm"
            >
              <VStack spacing={4} align="start">
                <Box
                  w={10}
                  h={10}
                  bg="purple.50"
                  borderRadius="lg"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={FiTarget} color="purple.600" boxSize={5} />
                </Box>
                <VStack align="start" spacing={2}>
                  <Text fontWeight="600" color="gray.900">
                    ATS Compatibility Testing
                  </Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="1.5">
                    Rigorous testing across 50+ applicant tracking systems 
                    to ensure optimal parsing and keyword recognition.
                  </Text>
                </VStack>
              </VStack>
            </Box>
          </SimpleGrid>
        </VStack>
      </Container>

      {/* CTA Section */}
      <Box bg="gray.900" py={{ base: "4rem", md: "6rem" }}>
        <Container maxW="6xl" px={{ base: 4, md: 8 }}>
          <VStack spacing="2rem" textAlign="center" maxW="600px" mx="auto">
            <Heading as="h2" size="xl" color="white" fontWeight="700">
              Ready to Experience the SkillBack Advantage?
            </Heading>
            <Text fontSize="lg" color="gray.300" lineHeight="1.6">
              Join thousands of professionals who've transformed their job search 
              with our research-backed approach
            </Text>
            <HStack spacing={4} pt={4}>
              <Button
                as={NavLink}
                to="/signup"
                colorScheme="blue"
                size="lg"
                px={8}
                py={6}
                fontSize="lg"
                fontWeight="600"
                borderRadius="xl"
                rightIcon={<Icon as={FiArrowRight} />}
                _focus={{ boxShadow: "none" }}
              >
                Start Free Trial
              </Button>
              <Button
                as={NavLink}
                to="/pricing"
                variant="outline"
                colorScheme="whiteAlpha"
                size="lg"
                px={6}
                py={6}
                fontSize="lg"
                fontWeight="500"
                borderRadius="xl"
                _focus={{ boxShadow: "none" }}
              >
                View Pricing
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};

export default WhyChooseUs;